import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Radio,
  RadioGroup,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { Tender, TenderAnswer } from '../../models/tendering';
import { User } from '../../models/users';
import { deepCloneObject } from '../../utils/clone';
import { updateTenderAnswer } from '../../services/tenderingService';

interface ChangeAnswerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (answer: TenderAnswer) => void;
  tender: Tender;
  question: string;
  answer: TenderAnswer;
  user: User;
}

type DialogState = 'open' | 'sent' | 'error';

const ChangeAnswerDialog: React.FC<ChangeAnswerDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  tender,
  question,
  answer,
  user,
}) => {
  const [originalAnswer] = useState<TenderAnswer>(answer);
  const [newAnswer, setNewAnswer] = useState<TenderAnswer>(deepCloneObject(answer));
  const [state, setState] = useState<DialogState>('open');

  useEffect(() => {
    if (isOpen) {
      const clonedAnswer = deepCloneObject(answer);
      if (clonedAnswer.value == null) {
        clonedAnswer.value = getDefaultAnswerValue(clonedAnswer.type);
      }
      setNewAnswer(clonedAnswer);
      setState('open');
    }
  }, [isOpen, answer]);

  const getDefaultAnswerValue = (type: string): any => {
    switch (type) {
      case 'string':
        return '';
      case 'decimalPrice':
        return 0;
      case 'decimalPriceLinear':
        return 0;
      case 'bool':
        return '0';
      default:
        return null;
    }
  };

  const handleSave = async () => {
    try {
      await updateTenderAnswer(tender.tenderID, newAnswer, user.userID);
      setState('sent');
      onSave(newAnswer);
    } catch (error) {
      console.error(error);
      setState('error');
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleOK = () => {
    onClose();
  };

  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleCancel}
              />
            }
          >
            Ändra Svar
          </DialogTitle>

          <DialogContent>
            {state === 'open' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">ID:</label>
                  <Input
                    type="number"
                    className="w-full"
                    value={newAnswer.questionID?.toString() || ''}
                    onChange={(e) =>
                      setNewAnswer({ ...newAnswer, questionID: Number(e.target.value) })
                    }
                  />
                </div>

                <p className="text-sm">{question}</p>

                {originalAnswer.type === 'string' && (
                  <div>
                    <h5 className="text-sm font-semibold mb-2">Svarsvärde:</h5>
                    <textarea
                      className="w-full border border-gray-300 rounded p-2 min-h-[180px]"
                      value={(newAnswer.value as string) || ''}
                      onChange={(e) => setNewAnswer({ ...newAnswer, value: e.target.value })}
                    />
                  </div>
                )}

                {originalAnswer.type === 'bool' && (
                  <div>
                    <h5 className="text-sm font-semibold mb-2">Svarsvärde:</h5>
                    <RadioGroup
                      value={newAnswer.value?.toString() || '0'}
                      onChange={(_, data) => setNewAnswer({ ...newAnswer, value: data.value })}
                    >
                      <Radio value="1" label="Ja" />
                      <Radio value="0" label="Nej" />
                    </RadioGroup>
                  </div>
                )}

                {(originalAnswer.type === 'decimalPriceLinear' ||
                  originalAnswer.type === 'decimalPrice') && (
                  <div>
                    <h5 className="text-sm font-semibold mb-2">Svarsvärde:</h5>
                    <Input
                      type="number"
                      className="w-full"
                      value={newAnswer.value?.toString() || '0'}
                      onChange={(e) =>
                        setNewAnswer({ ...newAnswer, value: Number(e.target.value) })
                      }
                    />
                  </div>
                )}

                <div>
                  <h5 className="text-sm font-semibold mb-2">Kommentar:</h5>
                  <Input
                    className="w-full"
                    value={newAnswer.comment || ''}
                    onChange={(e) => setNewAnswer({ ...newAnswer, comment: e.target.value })}
                  />
                </div>
              </div>
            )}

            {state === 'sent' && (
              <div className="py-4">
                <p className="text-sm">Din svarsändring är genomförd.</p>
              </div>
            )}

            {state === 'error' && (
              <div className="py-4">
                <p className="text-sm text-red-600">
                  Serverproblem... Försök igen senare eller kontakta support.
                </p>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {state === 'open' && (
              <>
                <Button appearance="secondary" onClick={handleCancel}>
                  Avbryt
                </Button>
                <Button appearance="primary" onClick={handleSave}>
                  Genomför svarsändring
                </Button>
              </>
            )}
            {state !== 'open' && (
              <Button appearance="primary" onClick={handleOK}>
                OK
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ChangeAnswerDialog;
